import React from "react";

import { Props } from "./types";
import styles from "./styles.scss";

function useRange(offset: number, edit: (data: number) => void) {
  const rangeRef = React.useRef<HTMLDivElement>(null);
  const [canMove, setCanMove] = React.useState<boolean>(false);
  const hasSelection = () => canMove;
  const resetSelection = () => setCanMove(false);
  const selectThumb = () => setCanMove(true);
  const update = (nextValue: number) => edit(nextValue);
  const getNextMoveValue = (rangeElement: HTMLDivElement, offset: number) => {
    const rect = rangeElement.getBoundingClientRect();
    const nextValue = Math.round(offset - rect.left);
    const rangeWidth = parseFloat(
      window.getComputedStyle(rangeElement).width as string,
    );

    return Math.round((nextValue / rangeWidth) * 100);
  };
  const canUpdate = (offset: number) => {
    const isBelowLimit = offset <= 100;
    const isAboveLimit = offset >= 0;

    return isBelowLimit && isAboveLimit;
  };

  React.useEffect(() => {
    const cancelSelection = () => {
      hasSelection() && resetSelection();
      document.documentElement.classList.remove(styles.unselectable);
    };
    const dispatchChange = (e: MouseEvent | TouchEvent) => {
      const mouseEvent = e as MouseEvent;
      const touchEvent = e as TouchEvent;

      hasSelection() &&
        !document.documentElement.classList.contains(styles.unselectable) &&
        document.documentElement.classList.add(styles.unselectable);

      if (rangeRef.current) {
        const nextValue = getNextMoveValue(
          rangeRef.current,
          !!mouseEvent.clientX
            ? mouseEvent.clientX
            : touchEvent.changedTouches.length > 0
              ? touchEvent.changedTouches[0].clientX
              : 0,
        );

        hasSelection() && canUpdate(nextValue) && update(nextValue);
      }
    };

    window.addEventListener("touchmove", dispatchChange);
    window.addEventListener("mousemove", dispatchChange);
    window.addEventListener("mouseup", cancelSelection);
    window.addEventListener("touchend", cancelSelection);

    return () => {
      window.removeEventListener("touchmove", dispatchChange);
      window.removeEventListener("mousemove", dispatchChange);
      window.removeEventListener("mouseup", cancelSelection);
      window.removeEventListener("touchend", cancelSelection);
    };
  });

  return { rangeRef, selectThumb };
}

function PanelResizer({ leftPanel, rightPanel, isMinimized }: Props) {
  const [width, setWidth] = React.useState(20);
  const { rangeRef, selectThumb } = useRange(width, setWidth);

  return (
    <div ref={rangeRef} className={styles.panelResizer}>
      <div
        className={styles.panel}
        style={
          isMinimized
            ? { width: "7rem", minWidth: "7rem" }
            : {
                width: `calc(${width}% - 0.5px)`,
                minWidth: "24rem",
              }
        }
      >
        {leftPanel}
      </div>
      {!isMinimized && (
        <div className={styles.track}>
          <div
            className={styles.thumb}
            onMouseDown={selectThumb}
            onTouchStart={selectThumb}
          />
        </div>
      )}
      <div
        className={styles.panel}
        style={
          isMinimized
            ? { width: `calc(100% - 7rem - 0.5px)` }
            : { width: `calc(${100 - width}% - 0.5px)` }
        }
      >
        {rightPanel}
      </div>
    </div>
  );
}

export { PanelResizer };
